import type { AnyTokenSymbol, Chain, NativeSendMode, SelfPaidTokenMode } from '@incpt/shared';
import { api } from './api';

// Re-exported rather than re-declared: these were a second copy of the shared
// unions, so a new chain or coin had to be spelled out here as well and the two
// could drift apart without anything failing. Every TMA screen imports them
// from this module, so the alias keeps those imports working.
export type { Chain };
export type Token = AnyTokenSymbol;
/** Stablecoin subset — card flows accept only these. */
export type StableToken = 'USDT' | 'USDC';
export type TransferStatus = 'pending' | 'submitted' | 'success' | 'failed';

export interface TransferDto {
  id: string;
  chain: Chain;
  token: Token;
  direction: 'in' | 'out';
  fromAddress: string;
  toAddress: string;
  /** Destination memo / tag as the server recorded it (XRP's tag today). */
  memo?: string;
  amount: string;
  feeUsd: string;
  status: TransferStatus;
  txHash: string | null;
  /** Why a failed send failed — 'insufficient_energy' → offer a Resend (higher energy). */
  failureReason: string | null;
  createdAt: string;
  purpose?: 'card_payment' | 'presale_payment' | 'send';
  cardPaymentKind?: 'issue' | 'topup';
}

/** Tells the API this send pays for a card, so it can price the network fee
 *  under the card fee policy. Re-verified server-side against the treasury
 *  recipient, the template and the amount. */
export type CardPaymentContext =
  | { kind: 'issue'; templateId: number; promoCode?: string; depositAmount?: number }
  | {
      kind: 'topup';
      /** Server-owned card id; supports legacy cards without a current template. */
      cardId: string;
      /** Top-up base amount (before our % fee) — required for the real send. */
      amount?: number;
    };

export interface CreateTransferParams {
  walletId: string;
  chain: Chain;
  token: Token;
  to: string;
  /** Decimal token amount as a string, e.g. "12.5". */
  amount: string;
  /** Source address to send from (defaults to the chain's primary). */
  fromAddressId?: string;
  cardPayment?: CardPaymentContext;
  /** Swap deposit — server may waive network fee from partner margin. */
  swapPartnerOrderId?: string;
  /**
   * Destination memo / tag, for chains that have one (XRP today).
   *
   * Needed when the recipient is a shared address — an exchange. Our own
   * receiving addresses are one per user, so nothing incoming needs one.
   */
  memo?: string;
}

export type HistoryFilter = { chain?: Chain; token?: Token };

export async function fetchTransfers(
  initData: string,
  filter?: HistoryFilter,
): Promise<TransferDto[]> {
  const qs = new URLSearchParams();
  if (filter?.chain) qs.set('chain', filter.chain);
  if (filter?.token) qs.set('token', filter.token);
  const q = qs.toString();
  const res = await api<{ transfers: TransferDto[] }>(initData, `/v1/transfers${q ? `?${q}` : ''}`);
  return res.transfers;
}

/** The card fee policy as applied to this quote — everything the sheet needs to
 *  re-derive the fee for the amount being typed without re-quoting. */
export interface CardFeeQuote {
  kind: 'issue' | 'topup';
  /** Network fee before the amount-based discount, warm-up gift excluded. */
  networkFeeUsd: number;
  chargedFeeUsd: number;
  /** % of the top-up amount available to pay the network fee down. 0 = none. */
  subsidyPct: number;
  providerFeePct: number;
  keepPct: number;
  /** Top-up amount from which the network fee is free (null = no discount). */
  freeFromUsd: number | null;
  halfFromUsd: number | null;
  /** One-time wallet warm-up (activation + approve) we are covering, in USD. */
  warmCoveredUsd: number;
  warmUsed: number;
  warmMaxPerUser: number;
}

export interface FeeQuote {
  feeUsd: number;
  /** Balance the send must leave for the fee, when it exceeds `feeUsd`. A first
   *  Tron send reserves headroom over the quote because its exact cost is only
   *  known after the one-time approve lands; gate Send on this, show `feeUsd`. */
  maxFeeUsd?: number;
  /** Natives only: the network fee in-kind (decimal amount + base units). */
  native?: { symbol: 'ETH' | 'BTC' | 'TRX'; amount: string; raw: string; decimals: number };
  /** BTC only: live sat/vB rates. */
  feeRates?: { fastestFee: number; halfHourFee: number; hourFee: number };
  /** Present only when the quote was asked for as a card payment. */
  cardFee?: CardFeeQuote;
  /** Swap deposit: network fee waived from partner margin. */
  swapNetworkFree?: boolean;
  /** Pre-waiver fee when `swapNetworkFree` is true. */
  fullFeeUsd?: number;
}

/** Estimated fee for a send on this chain — independent of amount. Stablecoins:
 *  USD ≈ token. Natives: network fee, both USD and in-kind. Pass the recipient
 *  (`to`) and sender (`from`) when known so the Tron estimate matches the real
 *  rental rather than a best-case guess, and `cardPayment` so a card fee is
 *  quoted under the same policy the send will be charged under.
 *  `swapFromAmount` asks whether ChangeHero partner margin covers a 1-leg deposit. */
export async function fetchFeeQuote(
  initData: string,
  chain: Chain,
  token: Token,
  to?: string,
  from?: string,
  cardPayment?: CardPaymentContext,
  swapFromAmount?: number,
): Promise<FeeQuote> {
  const q =
    (to ? `&to=${encodeURIComponent(to)}` : '') +
    (from ? `&from=${encodeURIComponent(from)}` : '') +
    (cardPayment
      ? `&cardKind=${cardPayment.kind}` +
        (cardPayment.kind === 'issue'
          ? `&cardTemplateId=${cardPayment.templateId}` +
            (cardPayment.promoCode
              ? `&cardPromoCode=${encodeURIComponent(cardPayment.promoCode)}`
              : '') +
            (cardPayment.depositAmount != null
              ? `&cardDepositAmount=${cardPayment.depositAmount}`
              : '')
          : `&cardId=${encodeURIComponent(cardPayment.cardId)}`) +
        (cardPayment.kind === 'topup' && cardPayment.amount
          ? `&cardAmount=${cardPayment.amount}`
          : '')
      : '') +
    (swapFromAmount != null && swapFromAmount > 0
      ? `&swapFromAmount=${encodeURIComponent(String(swapFromAmount))}`
      : '');
  return api<FeeQuote>(initData, `/v1/transfers/fee-quote?chain=${chain}&token=${token}${q}`);
}

/** POST /v1/transfers response. `mode:'client-7702'` means the TMA must run the
 *  sponsored op on-device (via the RPC proxy) and report the result back.
 *  A NativeSendMode carries NO treasury — the user pays only the network fee
 *  from their own coin balance. Those modes come from the shared table the API
 *  answers with, so a new chain cannot arrive under a mode declared only here. */
export interface CreateTransferResponse {
  transfer: TransferDto;
  mode?: 'client-7702' | 'client-tron' | 'client-tron-disperse' | NativeSendMode | SelfPaidTokenMode;
  treasury?: string;
  feeUsd?: number;
  /** Disperse mode: the multisend contract + whether a one-time approve is due. */
  disperseContract?: string;
  needsApproval?: boolean;
  /** First disperse send: exact simulation/rental must run after approval lands. */
  needsPreparation?: boolean;
  /** Server-pinned calldata. The client must broadcast these values rather than
   * mutable form state after an idempotency replay. */
  preparedOwner?: string;
  preparedRecipient?: string;
  preparedAmount?: string;
  preparedToken?: StableToken;
  /** Native modes: estimated network fee { base units as string, ≈USD }. */
  fee?: { native: string; usd: number };
  /** client-btc: live sat/vB rates for the client-side coin selection. */
  feeRates?: { fastestFee: number; halfHourFee: number; hourFee: number };
}

export async function createTransfer(
  initData: string,
  params: CreateTransferParams,
  reauthToken?: string,
  /** Stable across retries of the SAME send so the backend dedupes instead of
   *  treating each attempt as a new transfer (which re-rents energy / burns TRX).
   *  Caller mints one per send-intent and reuses it on retry. */
  idempotencyKey?: string,
): Promise<CreateTransferResponse> {
  const headers: Record<string, string> = {
    'Idempotency-Key': idempotencyKey ?? globalThis.crypto.randomUUID(),
  };
  if (reauthToken) headers['X-Reauth'] = reauthToken;
  return api<CreateTransferResponse>(initData, '/v1/transfers', {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });
}

/** After the first-time Tron disperse approval is confirmed, ask the server to
 * simulate the now-executable call, rent its exact resource deficit, and return
 * the authoritative fee used in the signed transaction. */
export function prepareTronTransfer(
  initData: string,
  transferId: string,
  reauthToken?: string,
): Promise<CreateTransferResponse> {
  const headers = reauthToken ? { 'X-Reauth': reauthToken } : undefined;
  return api<CreateTransferResponse>(initData, `/v1/transfers/${transferId}/tron-prepare`, {
    method: 'POST',
    headers,
  });
}

/** Persist then broadcast a client-signed atomic TRON transaction through the
 * API. The signed payload is durable before the first network broadcast. */
export async function broadcastTronTransfer(
  initData: string,
  transferId: string,
  signedTransaction: unknown,
  reauthToken?: string,
): Promise<TransferDto> {
  const headers = reauthToken ? { 'X-Reauth': reauthToken } : undefined;
  const res = await api<{ transfer: TransferDto }>(
    initData,
    `/v1/transfers/${transferId}/tron-broadcast`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ signedTransaction }),
    },
  );
  return res.transfer;
}

/** True when a thrown send error is a Tron energy/bandwidth shortfall. */
export function isResourceError(e: unknown): boolean {
  return /account resource insufficient|out_of_energy|onchain_reverted|(?:bandwith|bandwidth)_error|insufficient.*(energy|bandwidth)/i.test(
    String((e as Error)?.message ?? e),
  );
}

/** Report the on-chain result of a client-executed transfer.
 *
 * Pass `keepalive: true` for background (fire-and-forget) calls so the request
 * survives the WebView being closed before the response arrives. The body is
 * always small enough to satisfy the browser's keepalive size limit. */
export async function patchTransfer(
  initData: string,
  id: string,
  body: {
    txHash?: string;
    feeTxHash?: string;
    status?: 'failed';
    failureReason?: string;
    /** EIP-7702 UserOp accepted by the bundler; inclusion wait timed out. */
    userOpHash?: string;
  },
  opts?: { keepalive?: boolean },
): Promise<TransferDto> {
  const res = await api<{ transfer: TransferDto }>(initData, `/v1/transfers/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    keepalive: opts?.keepalive,
  });
  return res.transfer;
}
